/** @jsxImportSource @emotion/react */
import { Container, Icon, Link, Message, Stack, Text, useTheme } from "basis";
import { CardButton } from "components/CardButton/CardButton";
import { getSelfHref, useCreditCardAccount } from "library/accounts";
import {
  Account,
  EnhancedAccount,
  LastStatement,
} from "library/accounts/types";
import { Product } from "library/hooks/types";
import { useConfigFeature } from "library/hooks/useConfig";
import { formatDueDate } from "library/utils";
import { useEventTracking } from "react-event-tracker";
import { CardRibbon } from "../CardRibbon/CardRibbon";
import { CurrencyFormat } from "../CurrencyFormat";
import { Separator } from "../Separator/Separator";
import styles from "./credit-card-tile.module.scss";

// TODO: Convert to camelCase keys
type PaymentInfo = {
  accountNumber?: string;
  accountId: string;
  productId: string;
  minimum_monthly_repayment: number;
  current_balance: number;
  amount_immediate_due: number;
  payment_due_date: string;
  payment_due: string;
  last_statement: LastStatement;
  payinEligible: boolean;
  accountNum: string;
  bsb: string;
  lastFour: string;
  creditLimit: number;
};

const AccountDisabled = () => (
  <Container padding="5" testId="disabled_card">
    <Message severity="info-or-minor" bg="transparent" padding="0">
      Your credit card has been disabled. Please contact support.
    </Message>
  </Container>
);

type MakePaymentBtnProps = {
  paymentInfo: PaymentInfo;
};

const MakePaymentBtn = ({ paymentInfo }: MakePaymentBtnProps) => {
  const { trackEvent } = useEventTracking();

  return (
    <Link
      appearance="primary-button"
      variant="blue-button"
      width="100%"
      href="/ways-to-pay"
      newTab={false}
      state={{ paymentInfo }}
      onClick={() => {
        trackEvent({
          event: {
            category: "text-link",
            action: "internal-link",
            label: "View account",
            location: "overview",
          },
        });
      }}
    >
      Make a payment
    </Link>
  );
};

type NoMinimumPaymentProps = {
  paymentInfo: PaymentInfo;
};

const NoMinimumPayment = ({ paymentInfo }: NoMinimumPaymentProps) => (
  <Container padding="0 5 5" testId="no_pay">
    <Stack gap="4">
      <Separator />
      <Message severity="success" bg="transparent" padding="0">
        No minimum payment required.
      </Message>
      {paymentInfo.current_balance < 0 ? null : (
        <MakePaymentBtn paymentInfo={paymentInfo} />
      )}
    </Stack>
  </Container>
);

type MonthlyPaymentProps = {
  paymentInfo: PaymentInfo;
};

const MonthlyPayment = ({ paymentInfo }: MonthlyPaymentProps) => (
  <Container padding="0 5 5" testId="minimum_payment">
    <Stack gap="4">
      <Separator />
      <>
        <Text textStyle="body2" color="grey.t75">
          Minimum payment due
        </Text>
        <Text textStyle="heading5" color="black">
          <strong>{formatDueDate(paymentInfo.payment_due)}</strong>
        </Text>
      </>
      <MakePaymentBtn paymentInfo={paymentInfo} />
    </Stack>
  </Container>
);

type OverduePaymentProps = {
  paymentInfo: PaymentInfo;
};

const OverduePayment = ({ paymentInfo }: OverduePaymentProps) => (
  <Container padding="0 5 5" testId="overdue_payment">
    <Stack gap="4">
      <Separator />
      <Message severity="warning-or-significant" bg="secondary.pink.t30">
        You have an overdue payment.
      </Message>
      <MakePaymentBtn paymentInfo={paymentInfo} />
    </Stack>
  </Container>
);

type ViewAccountProps = {
  account: Account;
  product: Product;
};

const ViewAccount = ({ account, product }: ViewAccountProps) => (
  <CardButton
    href="/credit-card-account"
    state={{ account, product }}
    testId="view-account"
    eventData={{
      category: "text-link",
      action: "internal-link",
      label: "View account",
      location: "overview",
      value: account.account_id,
    }}
  >
    <Text textStyle="subtitle2" color="primary.blue.t100">
      <strong>View account</strong>
    </Text>
    <Icon name="chevron-right" color="primary.blue.t100" />
  </CardButton>
);

type ActivateCardProps = {
  account: Account;
};

const ActivateCard = ({ account }: ActivateCardProps) => {
  const { trackEvent } = useEventTracking();

  return (
    <Container padding="0 5 5">
      <Stack gap="4">
        <Separator />
        <Text>Received your new card in the mail?</Text>
        <Link
          appearance="secondary-button"
          variant="blue-button"
          width="100%"
          href="/activate-card"
          newTab={false}
          state={{ account }}
          onClick={() => {
            trackEvent({
              event: {
                category: "text-link",
                action: "internal-link",
                label: "Activate card",
                location: "overview",
              },
            });
          }}
        >
          Activate card
        </Link>
      </Stack>
    </Container>
  );
};

type Props = {
  account: EnhancedAccount;
  product: Product;
};

export const CreditCardTile = (props: Props) => {
  const theme = useTheme();
  const { product } = props;
  const href = getSelfHref(props.account);
  const accountInfo = useCreditCardAccount({
    accountId: props.account.account_id,
    enabled: href !== undefined,
    initialData: props.account,
  });
  const activationEnabled = useConfigFeature("cardActivation");
  const account = accountInfo.data;
  const creditCard = account?.credit_card;

  if (!account || !creditCard) {
    return null;
  }

  const cardDisabled = account.links.length === 0;
  const cardActivatable =
    account.links.some((e) => e.rel === "card_activation") || false;
  const paymentInfo: PaymentInfo = {
    accountNumber: account.account_number,
    accountId: account.account_id,
    productId: account.product_id,
    minimum_monthly_repayment: creditCard.minimum_monthly_repayment,
    current_balance: creditCard.current_balance,
    amount_immediate_due: creditCard.amount_immediate_due,
    payment_due_date: creditCard.payment_due_date,
    payment_due: creditCard.payment_due,
    last_statement: creditCard.last_statement,
    payinEligible: account.payin?.status === "ELIGIBLE",
    accountNum: account.payin?.bank_account ?? "",
    bsb: account.payin?.bsb ?? "",
    lastFour: creditCard.linked_card_last4,
    creditLimit: creditCard.credit_limit,
  };
  const ribbonText =
    account.status === "CLOSED" ? "CLOSED" : cardDisabled ? "DISABLED" : null;

  return (
    <div
      className={styles.tile}
      data-testid={`credit-card-ending-${creditCard.linked_card_last4}`}
    >
      {ribbonText && (
        <div
          data-testid={
            account.status === "CLOSED" &&
            `credit-card-ending-${creditCard.linked_card_last4}-ribbon`
          }
        >
          <CardRibbon text={ribbonText} />
        </div>
      )}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "4px 4px 0px 0px",
          backgroundColor: theme.colors.primary.blue.t100,
          height: 108,
          padding: `${theme.space[4]} ${
            ribbonText
              ? "68px" /* This makes sure that our longest credit card name ("Latitude 28 Degrees Global Platinum Mastercard") doesn't wrap to the third line. */
              : theme.space[5]
          } ${theme.space[4]} ${theme.space[5]}`,
        }}
      >
        {product && (
          <>
            <Text textStyle="heading5" color="white">
              {product.name}
            </Text>
            {creditCard.linked_card_last4 && (
              <Text textStyle="body2" color="white" margin="auto 0 0 0">
                Card ending {creditCard.linked_card_last4}
              </Text>
            )}
          </>
        )}
      </div>
      {!accountInfo.isError && !cardDisabled && (
        <div className={styles.overview}>
          <div className={styles.row}>
            <div>
              <div>Current balance</div>
              <strong>
                <CurrencyFormat value={creditCard.current_balance} />
              </strong>
            </div>
            <div>
              <div className={styles.right}>Available to spend</div>
              <div className={styles.right}>
                <strong>
                  <CurrencyFormat value={creditCard.available_balance} />
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
      {accountInfo.isError ? (
        <Container padding="5">
          <Message severity="blocking" bg="transparent" padding="0">
            Unable to retrieve your account details. Please try again later.
          </Message>
        </Container>
      ) : cardDisabled ? (
        <AccountDisabled />
      ) : creditCard.current_balance ===
        0 ? null : creditCard.amount_immediate_due > 0 ? (
        <OverduePayment paymentInfo={paymentInfo} />
      ) : creditCard.minimum_monthly_repayment === 0 ? (
        <NoMinimumPayment paymentInfo={paymentInfo} />
      ) : (
        <MonthlyPayment paymentInfo={paymentInfo} />
      )}
      {cardActivatable && activationEnabled && (
        <ActivateCard account={account} />
      )}
      {!accountInfo.isError && !cardDisabled && (
        <ViewAccount account={account} product={product} />
      )}
    </div>
  );
};
